import React, { useState, useEffect } from 'react';
import { MdSearch, MdEdit, MdDelete, MdCheckCircle, MdBlock } from 'react-icons/md';
import { FiChevronLeft, FiChevronRight, FiFilter, FiDollarSign, FiShoppingCart, FiFileText, FiEye } from 'react-icons/fi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';

interface Invoice {
  id: number;
  invoice_number: string;
  user_id: number;
  user_email: string;
  user_name: string;
  type: 'order' | 'invoice';
  status: 'pending' | 'completed' | 'cancelled';
  package_id: number;
  package_name: string;
  package_period: string;
  // New pricing fields
  package_price: number;
  discount_percentage: number;
  discount_amount: number;
  tva_amount: number;
  total_to_pay: number;
  // Legacy amount field
  amount: number;
  discount?: string;
  // Dates
  order_date: string;
  invoice_date: string | null;
  payment_datetime: string | null;
  starting_date: string | null;
  ending_date: string | null;
  // Payment
  payment_method: string | null;
  payment_id: string | null;
  // Details
  client_details: {
    name_complete: string;
    phone: string;
    email: string;
  };
  organisation_details: {
    org_name: string;
    address: string;
    country: string;
    org_phone: string;
    zipcode: string;
    city: string;
    vat_number: string;
    company_size: string;
  };
  teamther_details: {
    company_name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;
    vat_number: string;
    vat_percentage: number;
  };
  package_features?: Array<{
    key: string;
    title_en: string;
    title_fr: string;
    value: any;
    value_type: 'boolean' | 'integer' | 'text';
    is_static: boolean;
    display_order: number;
  }>;
}

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'order' | 'invoice'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(30);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'success';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'danger', onConfirm: () => {} });
  const [stats, setStats] = useState({
    totalIncome: 0,
    numberOfOrders: 0,
    numberOfInvoices: 0,
  });

  // Format date to DD/MM/YYYY HH:MM
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };

  // Impersonation handler - redirect to billing page
  const handleImpersonateUser = async (userId: number) => {
    try {
      const response = await apiService.impersonateUser(userId);
      
      if (response.success) {
        const { access_token, refresh_token, admin_id, admin_email } = response.data;
        
        const impersonationData = {
          access_token,
          refresh_token,
          admin_id,
          admin_email,
        };
        
        const encodedData = btoa(JSON.stringify(impersonationData));
        
        // Redirect to FrontEnd billing page with impersonation token
        const frontendUrl = `https://app.teamther.ai/billing?impersonate=${encodedData}`;
        window.location.href = frontendUrl;
      }
    } catch (error: any) {
      setToast({ message: `Impersonation failed: ${error.message}`, type: 'error' });
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAllInvoices({
        search: searchQuery,
        type: filterType !== 'all' ? filterType : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: currentPage,
        page_size: pageSize,
      });

      // DRF pagination returns {count, results} directly
      if (response && response.results) {
        setInvoices(response.results);
        setTotalCount(response.count);
        setTotalPages(Math.ceil(response.count / pageSize));
      }
    } catch (error: any) {
      setToast({ message: `Error fetching invoices: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllInvoicesForStats = async () => {
    try {
      // Fetch all invoices with same filters for accurate stats
      const response = await apiService.getAllInvoices({
        search: searchQuery,
        type: filterType !== 'all' ? filterType : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page_size: 10000, // Large number to get all filtered records
      });

      if (response && response.results) {
        const allInvoices = response.results;
        
        // Calculate total income from completed invoices only
        const totalIncome = allInvoices
          .filter(inv => inv.status === 'completed' && inv.type === 'invoice')
          .reduce((sum, inv) => sum + inv.amount, 0);
        
        // Count orders and invoices
        const numberOfOrders = allInvoices.filter(inv => inv.type === 'order').length;
        const numberOfInvoices = allInvoices.filter(inv => inv.type === 'invoice').length;
        
        setStats({
          totalIncome,
          numberOfOrders,
          numberOfInvoices,
        });
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchAllInvoicesForStats(); // Fetch stats separately
  }, [searchQuery, filterType, startDate, endDate, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleDeleteInvoice = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Invoice',
      message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiService.deleteInvoice(id);
          setToast({ message: 'Invoice deleted successfully', type: 'success' });
          fetchInvoices();
        } catch (error: any) {
          setToast({ message: `Error deleting invoice: ${error.message}`, type: 'error' });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const getTypeBadge = (type: string) => {
    return type === 'invoice'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-purple-100 text-purple-700';
  };

  const handleViewInvoice = (invoice: Invoice) => {
    // Default to English for admin panel
    generateInvoicePDF(invoice as any, 'en');
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Subscriptions & Invoices
          </h1>
          
        </div>

        {/* Filters */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
          <div className="flex items-center mb-4">
            <FiFilter className="mr-2 h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Filters
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="lg:col-span-2">
              <div className="relative">
                <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by user name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
            </form>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as any);
                setCurrentPage(1);
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="order">Order</option>
              <option value="invoice">Invoice</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Total Income */}
          <div className="rounded-lg bg-gradient-to-br from-green-500 to-green-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-100">Total Income</p>
                <p className="mt-2 text-3xl font-bold text-white">€{stats.totalIncome.toFixed(2)}</p>
                <p className="mt-1 text-xs text-green-100">From completed invoices</p>
              </div>
              <div className="rounded-full bg-white/20 p-3">
                <FiDollarSign className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          {/* Number of Orders */}
          <div className="rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-100">Orders</p>
                <p className="mt-2 text-3xl font-bold text-white">{stats.numberOfOrders}</p>
                <p className="mt-1 text-xs text-purple-100">Total orders</p>
              </div>
              <div className="rounded-full bg-white/20 p-3">
                <FiShoppingCart className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          {/* Number of Invoices */}
          <div className="rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-100">Invoices</p>
                <p className="mt-2 text-3xl font-bold text-white">{stats.numberOfInvoices}</p>
                <p className="mt-1 text-xs text-blue-100">Total invoices</p>
              </div>
              <div className="rounded-full bg-white/20 p-3">
                <FiFileText className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Showing <span className="font-bold">{invoices.length}</span> of{' '}
          <span className="font-bold">{totalCount}</span> invoices
        </div>

        {/* Invoices Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow-md dark:bg-navy-800">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Invoice #
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    User
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Package
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Amount
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Order Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Payment Date
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-navy-600">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center">
                      <div className="flex items-center justify-center">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-t-transparent mr-2"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-gray-600 dark:text-gray-400">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 dark:hover:bg-navy-700 transition"
                    >
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
                          {invoice.invoice_number}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div
                          className="cursor-pointer"
                          onClick={() => handleImpersonateUser(invoice.user_id)}
                          title="Click to view as user"
                        >
                          <p className="font-medium text-navy-800 dark:text-white">
                            {invoice.user_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {invoice.user_email}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${getTypeBadge(
                            invoice.type
                          )}`}
                        >
                          {invoice.type}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {invoice.package_name}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400">
                            {invoice.package_period}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          €{invoice.amount}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(invoice.order_date)}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(invoice.payment_datetime)}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="rounded-lg bg-blue-100 p-2 text-blue-700 transition hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                            title="View Invoice PDF"
                          >
                            <FiEye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/subscriptions/edit/${invoice.id}`)}
                            className="rounded-lg bg-yellow-100 p-2 text-yellow-700 transition hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:hover:bg-yellow-900/50"
                            title="Edit Invoice"
                          >
                            <MdEdit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            className="rounded-lg bg-red-100 p-2 text-red-700 transition hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                            title="Delete Invoice"
                          >
                            <MdDelete className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="flex items-center space-x-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                <FiChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                <span>Next</span>
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        />
      </div>
    </Layout>
  );
};

export default Subscriptions;
